import { PromoType } from '@/components/blocks/FreeGiftV2/constants';
import {
	PromotionCombination,
	PromotionGroup,
	PurchaseGroup,
	PurchaseTarget,
	RewardGroup,
	RewardTarget,
	SkuQuantity,
} from '@/components/blocks/FreeGiftV2/types';
import { ProductType } from '@/data/types/Product';
import { CombinablePromotion } from '@/data/types/Promotions';
import { CDN_BASE_URL } from 'shared/custom/constants/commonConstants';

export interface StoredFreeGiftSelection {
	selectedPurchaseGroupId?: string;
	selectedPurchaseSkus: SkuQuantity[];
	selectedRewardGroupId?: string;
	selectedRewardSkus: SkuQuantity[];
	promotionQty: number;
	isDirect: boolean;
	addedFromPdp?: boolean;
	promoThdCode?: PromoType;
}

const freeGiftSelectionsByPartNumber = new Map<string, StoredFreeGiftSelection>();

export const readStoredFreeGiftSelection = (
	currentPartNumber: string
): StoredFreeGiftSelection | undefined => {
	if (typeof window === 'undefined') {
		return undefined;
	}

	return freeGiftSelectionsByPartNumber.get(currentPartNumber);
};

export const saveFreeGiftSelection = (
	currentPartNumber: string,
	selection: StoredFreeGiftSelection
): void => {
	if (typeof window === 'undefined') {
		return;
	}

	freeGiftSelectionsByPartNumber.set(currentPartNumber, selection);
};

export const clearFreeGiftSelection = (currentPartNumber: string): void => {
	if (typeof window === 'undefined') {
		return;
	}

	freeGiftSelectionsByPartNumber.delete(currentPartNumber);
};

export const markFreeGiftSelectionAddedFromPdp = (currentPartNumber: string): void => {
	const stored = readStoredFreeGiftSelection(currentPartNumber);

	if (stored) {
		saveFreeGiftSelection(currentPartNumber, { ...stored, addedFromPdp: true });
	}
};

export const FREE_GIFT_ADDED_FROM_PDP_EVENT = 'freeGiftPromotionAddedFromPdp';

export interface FreeGiftAddedFromPdpEventDetail {
	partNumber: string;
}

export const notifyFreeGiftAddedFromPdp = (partNumber: string): void => {
	if (typeof window === 'undefined') {
		return;
	}

	window.dispatchEvent(
		new CustomEvent<FreeGiftAddedFromPdpEventDetail>(FREE_GIFT_ADDED_FROM_PDP_EVENT, {
			detail: { partNumber },
		})
	);
};

export const clearStaleFreeGiftSelections = (currentPartNumber: string): void => {
	if (typeof window === 'undefined') {
		return;
	}

	Array.from(freeGiftSelectionsByPartNumber.keys())
		.filter((key) => key !== currentPartNumber)
		.forEach((key) => freeGiftSelectionsByPartNumber.delete(key));
};

const TNC_TOKEN = 'TNC';

export const getPromoTypeFromName = (promotionName?: string | null): PromoType | undefined => {
	const tokens = promotionName?.split('-') ?? [];

	if (tokens.includes(PromoType.PGNC)) {
		return tokens.includes(TNC_TOKEN) ? PromoType.TNC_PGNC : PromoType.PGNC;
	}

	if (tokens.includes(PromoType.PGC)) {
		return tokens.includes(TNC_TOKEN) ? PromoType.TNC_PGC : PromoType.PGC;
	}

	if (tokens.includes(PromoType.PG)) {
		return PromoType.PG;
	}

	return undefined;
};

export const classifyCombinablePromotions = (
	combinablePromotions: CombinablePromotion[] | null | undefined
): string | undefined => {
	const codes = new Set(
		filterValidCombinablePromotions(combinablePromotions ?? [])
			.map((promo) => getPromoTypeFromName(promo.PromotionName))
			.filter((code): code is PromoType => code !== undefined)
	);

	return codes.size > 0 ? Array.from(codes).join(',') : undefined;
};

export const getProductDetailsByPartNumber = (
	promoItems: ProductType[],
	partNumber: string
): ProductType | undefined => promoItems?.find((item) => item.partNumber === partNumber);

export const generateImageUrl = (
	partNumber?: string | undefined,
	isMobile?: boolean | undefined
): string =>
	`${CDN_BASE_URL}/productos/${partNumber?.replace('P', '')}/${partNumber?.replace('P', '')}${
		isMobile ? '-m' : ''
	}.jpg`;

export const combinePromotions = (promotions: CombinablePromotion[]): PromotionGroup[] => {
	const groups: PromotionGroup[] = [];

	promotions.forEach((promo) => {
		const promoId = promo.promotionId;

		const promoTriggerGroupId = promo.purchase.triggerGroupIds[0];
		const promoTargetGroupId = promo.reward.targetGroupIds[0];

		const existingGroup = groups.find((group) => {
			const groupTriggerIds = group.purchases.map((p) => p.triggerGroupId);
			const groupTargetIds = group.rewards.map((r) => r.targetGroupId);

			const idsMatch =
				groupTriggerIds.includes(promoTriggerGroupId) ||
				groupTargetIds.includes(promoTargetGroupId);

			if (!idsMatch) {
				return false;
			}

			const purchaseCombinableMatches = group.purchases.every(
				(p) => p.combinable === promo.purchase.combinable
			);
			const rewardCombinableMatches = group.rewards.every(
				(r) => r.combinable === promo.reward.combinable
			);

			return purchaseCombinableMatches && rewardCombinableMatches;
		});

		if (!existingGroup) {
			groups.push({
				purchases: [
					{
						triggerGroupId: promoTriggerGroupId,
						triggerGroupName: promo.purchase.triggerGroupName,
						quantity: promo.purchase.quantity,
						triggerSKUs: promo.purchase.triggerSKUs,
						combinable: promo.purchase.combinable,
						promotionIds: [promoId],
					},
				],
				rewards: [
					{
						targetGroupId: promoTargetGroupId,
						targetGroupName: promo.reward.targetGroupName,
						quantity: promo.reward.quantity,
						targetSKUs: promo.reward.targetSKUs,
						combinable: promo.reward.combinable,
						promotionIds: [promoId],
					},
				],
			});
			return;
		}

		const purchase = existingGroup.purchases.find((p) => p.triggerGroupId === promoTriggerGroupId);

		if (!purchase) {
			existingGroup.purchases.push({
				triggerGroupId: promoTriggerGroupId,
				triggerGroupName: promo.purchase.triggerGroupName,
				quantity: promo.purchase.quantity,
				triggerSKUs: promo.purchase.triggerSKUs,
				combinable: promo.purchase.combinable,
				promotionIds: [promoId],
			});
		} else if (!purchase.promotionIds.includes(promoId)) {
			purchase.promotionIds.push(promoId);
		}

		const reward = existingGroup.rewards.find((r) => r.targetGroupId === promoTargetGroupId);

		if (!reward) {
			existingGroup.rewards.push({
				targetGroupId: promoTargetGroupId,
				targetGroupName: promo.reward.targetGroupName,
				quantity: promo.reward.quantity,
				targetSKUs: promo.reward.targetSKUs,
				combinable: promo.reward.combinable,
				promotionIds: [promoId],
			});
		} else {
			if (!reward.promotionIds.includes(promoId)) {
				reward.promotionIds.push(promoId);
			}

			reward.targetSKUs = Array.from(
				new Map([...reward.targetSKUs, ...promo.reward.targetSKUs].map((t) => [t.sku, t])).values()
			);
		}
	});

	return groups;
};

const isPGOnlyPromotion = (promotionName?: string | null): boolean =>
	(promotionName?.split('-') ?? []).includes(PromoType.PG);

const buildPGPromotionGroup = (
	promo: CombinablePromotion,
	currentPartNumber: string,
	isRewardView: boolean
): PromotionCombination => {
	const promoThdCode = getPromoTypeFromName(promo.PromotionName);
	const parsedBaseQty = promo.purchase?.quantity ?? 0;
	const parsedGiftQty = promo.reward?.quantity ?? 0;

	if (isRewardView) {
		const triggerSKUs = promo.purchase?.triggerSKUs ?? [];

		const purchase: PurchaseGroup = {
			combinable: true,
			purchases: triggerSKUs.map(({ sku }) => ({
				triggerGroupId: sku,
				quantity: parsedBaseQty,
				triggerSKUs: [{ sku, quantity: parsedBaseQty }],
			})),
		};

		const reward: RewardGroup = {
			combinable: false,
			rewards: [
				{
					targetGroupId: currentPartNumber,
					quantity: parsedGiftQty,
					targetSKUs: [{ sku: currentPartNumber, quantity: parsedGiftQty }],
				},
			],
		};

		return { purchase, reward, promoThdCode };
	}

	const targetSKUs = promo.reward?.targetSKUs ?? [];

	const reward: RewardGroup = {
		combinable: false,
		rewards: targetSKUs.map(({ sku }) => ({
			targetGroupId: sku,
			quantity: parsedGiftQty,
			targetSKUs: [{ sku, quantity: parsedGiftQty }],
		})),
	};

	const purchase: PurchaseGroup = {
		combinable: true,
		purchases: [
			{
				triggerGroupId: currentPartNumber,
				quantity: parsedBaseQty,
				triggerSKUs: [{ sku: currentPartNumber, quantity: parsedBaseQty }],
			},
		],
	};

	return { purchase, reward, promoThdCode };
};

export const getCombinablePromotions = (
	promotions: CombinablePromotion[],
	currentPartNumber = '',
	isRewardView = false
): PromotionCombination[] => {
	const pgPromotions = promotions.filter((promo) => isPGOnlyPromotion(promo.PromotionName));
	const otherPromotions = promotions.filter((promo) => !isPGOnlyPromotion(promo.PromotionName));

	const pgGroups = pgPromotions.map((promo) =>
		buildPGPromotionGroup(promo, currentPartNumber, isRewardView)
	);

	const otherPromos = combinePromotions(otherPromotions);

	const otherGroups = otherPromos.map((promo: PromotionGroup): PromotionCombination => {
		const firstPromotionId = promo.purchases[0]?.promotionIds[0];
		const promoThdCode = getPromoTypeFromName(
			otherPromotions.find(({ promotionId }) => promotionId === firstPromotionId)?.PromotionName
		);

		const reward: RewardGroup = {
			combinable: Boolean(promo.rewards[0]?.combinable),
			rewards: promo.rewards.map(({ targetGroupId, quantity, targetSKUs }) => ({
				targetGroupId,
				quantity,
				targetSKUs,
			})),
		};

		const purchase: PurchaseGroup = {
			combinable: Boolean(promo.purchases[0]?.combinable),
			purchases: promo.purchases.map(({ triggerGroupId, quantity, triggerSKUs }) => ({
				triggerGroupId,
				quantity,
				triggerSKUs,
			})),
		};

		return { purchase, reward, promoThdCode };
	});

	return [...pgGroups, ...otherGroups];
};

const getRewardGroupQuantity = (group: RewardTarget) =>
	group.targetSKUs.reduce((sum, sku) => sum + (sku.quantity || 0), 0);

export const getTargetQuantity = ({ combinable, rewards }: RewardGroup) => {
	if (rewards.length === 1) {
		const group = rewards[0];

		return {
			rewardsQty: combinable ? group.quantity : getRewardGroupQuantity(group),
			showUpToLabel: false,
		};
	}

	const quantities = rewards.map(getRewardGroupQuantity);

	return {
		rewardsQty: quantities.reduce((max, total) => Math.max(max, total), 0),
		showUpToLabel: new Set(quantities).size > 1,
	};
};

export const getPurchasesQuantities = (
	{ combinable, purchases }: PurchaseGroup,
	parentSku: string
) => {
	const group = purchases[0];

	if (!group) {
		return {
			parentQty: 0,
			othersQty: 0,
		};
	}

	if (combinable) {
		return {
			parentQty: 1,
			othersQty: Math.max((group.quantity || 0) - 1, 0),
		};
	}

	return group.triggerSKUs.reduce(
		(acc, sku) => {
			if (sku.sku === parentSku) {
				acc.parentQty += sku.quantity || 0;
			} else {
				acc.othersQty += sku.quantity || 0;
			}
			return acc;
		},
		{
			parentQty: 0,
			othersQty: 0,
		}
	);
};

export const getRewardsQuantities = ({ combinable, rewards }: RewardGroup, parentSku: string) => {
	const group = rewards[0];

	if (!group) {
		return {
			parentQty: 0,
			othersQty: 0,
		};
	}

	if (combinable) {
		return {
			parentQty: 1,
			othersQty: Math.max((group.quantity || 0) - 1, 0),
		};
	}

	return group.targetSKUs.reduce(
		(acc, sku) => {
			if (sku.sku === parentSku) {
				acc.parentQty += sku.quantity || 0;
			} else {
				acc.othersQty += sku.quantity || 0;
			}
			return acc;
		},
		{
			parentQty: 0,
			othersQty: 0,
		}
	);
};

export type MarketInventoryIndex = Record<string, number | undefined>;

export const getMarketInventory = (
	product: ProductType | undefined,
	marketId: string | undefined
): number | undefined => {
	if (!product || !marketId) return undefined;

	const raw = product[`inventories.M${marketId}.quantity`];
	const quantity = raw === undefined || raw === null || raw === '' ? undefined : Number(raw);
	const realQuantity = quantity === undefined || Number.isNaN(quantity) ? undefined : quantity;

	return realQuantity;
};

export const isOutOfStock = (quantity: number | undefined): boolean => quantity === 0;

/** `undefined` (the index omits the key) is treated as "unknown, assume available", matching every other consumer of this same indexed-inventory key. */
export const hasEnoughInventory = (quantity: number | undefined, required: number): boolean =>
	quantity === undefined || quantity >= required;

export const buildMarketInventoryIndex = (
	promoItems: ProductType[],
	marketId: string | undefined
): MarketInventoryIndex =>
	promoItems.reduce<MarketInventoryIndex>((index, product) => {
		if (product.partNumber) {
			index[product.partNumber] = getMarketInventory(product, marketId);
		}
		return index;
	}, {});

export interface DeficientSku {
	sku: string;
	available: number | undefined;
	required: number;
}

export const getDeficientSkus = (
	skus: SkuQuantity[],
	promotionQty: number,
	inventory: MarketInventoryIndex
): DeficientSku[] =>
	skus.reduce<DeficientSku[]>((acc, { sku, quantity }) => {
		const required = quantity * promotionQty;
		const available = inventory[sku];

		if (!hasEnoughInventory(available, required)) {
			acc.push({ sku, available, required });
		}

		return acc;
	}, []);

export type InsufficientMessageKind = 'none' | 'sku' | 'generic';

export const getFixedGroupMessageKind = (
	deficientSkus: DeficientSku[]
): InsufficientMessageKind => {
	if (deficientSkus.length === 0) return 'none';

	if (deficientSkus.length === 1 && (deficientSkus[0].available ?? 0) > 0) return 'sku';

	return 'generic';
};

export const isFixedRewardGroupOutOfStock = (
	group: RewardTarget,
	promotionQty: number,
	inventory: MarketInventoryIndex
): boolean => getDeficientSkus(group.targetSKUs, promotionQty, inventory).length > 0;

export const isFixedPurchaseGroupOutOfStock = (
	group: PurchaseTarget,
	promotionQty: number,
	inventory: MarketInventoryIndex
): boolean => getDeficientSkus(group.triggerSKUs, promotionQty, inventory).length > 0;

export const getCombinableAchievableQty = (
	candidates: SkuQuantity[],
	requiredQty: number,
	inventory: MarketInventoryIndex
): number =>
	candidates.reduce((sum, { sku }) => {
		const available = inventory[sku];

		return sum + Math.min(available ?? requiredQty, requiredQty);
	}, 0);

export const getCombinableMessageKind = (
	totalCandidateCount: number,
	isDeficient: boolean
): InsufficientMessageKind => {
	if (!isDeficient) return 'none';

	return totalCandidateCount === 1 ? 'sku' : 'generic';
};

export const isEveryGiftOutOfStock = (
	reward: RewardGroup,
	promotionQty: number,
	inventory: MarketInventoryIndex
): boolean => {
	const groups = reward?.rewards ?? [];

	if (groups.length === 0) return false;

	if (reward.combinable) {
		const candidates = groups[0]?.targetSKUs ?? [];
		return candidates.length > 0 && candidates.every((sku) => isOutOfStock(inventory[sku.sku]));
	}

	return groups.every((group) => isFixedRewardGroupOutOfStock(group, promotionQty, inventory));
};

export const filterValidCombinablePromotions = (
	promotions: CombinablePromotion[]
): CombinablePromotion[] =>
	promotions.filter(
		(promo) =>
			promo.purchase &&
			typeof promo.purchase === 'object' &&
			Object.keys(promo.purchase).length > 0 &&
			promo.reward &&
			typeof promo.reward === 'object' &&
			Object.keys(promo.reward).length > 0
	);
